import { useEffect, useRef, useState } from 'react';
import { Form, Input, Button } from 'antd';
import {
  LockOutlined,
  MailOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined,
  ArrowLeftOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { sanitizeEmail } from '../../utils/sanitize';
import { notifyError } from '../../utils/notify';

// Login-specific badge — same brand-blue rounded square the rest of the
// app uses for brand marks, but with a meaningful glyph for the surface
// (a shield+check for the password step, a key for the MFA step). We
// keep it inline here instead of mutating the global <BrandMark> so
// other surfaces (signup, public shell) stay on the corporate logo.
function LoginBadge({ icon, className = '' }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center rounded-xl bg-brand-500 text-white shadow-[0_8px_20px_-6px_rgba(24,119,242,0.55)] text-2xl ${className}`}
    >
      {icon}
    </span>
  );
}

export default function LoginPage() {
  const { loginByEmail, verifyLoginMfa } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Stage 5M: when the backend returns MFA_REQUIRED, loginByEmail
  // resolves to a { mfaRequired, challengeId } object instead of
  // throwing. We capture it here and switch the form into a code-entry
  // step. `null` = normal email/password step.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const codeInputRef = useRef(null);
  const [mfaCode, setMfaCode] = useState('');

  // Focus the 6-digit input as soon as the MFA step mounts.
  useEffect(() => {
    if (!mfaChallenge) return;
    const t = setTimeout(() => {
      try {
        codeInputRef.current?.focus?.();
      } catch (_e) {
        /* noop */
      }
    }, 80);
    return () => clearTimeout(t);
  }, [mfaChallenge]);

  async function onFinish(values) {
    setSubmitting(true);
    try {
      // Lowercase + trim email through the centralised sanitiser; the
      // password is passed through unchanged (we never silently mutate
      // a typed password — the form rule rejects leading/trailing
      // whitespace explicitly via passwordRule).
      const result = await loginByEmail(
        sanitizeEmail(values.email),
        values.password
      );
      // Stage 5M: MFA branch — switch to the code step instead of
      // navigating. AuthContext returns { mfaRequired:true, challengeId }
      // for this case; a normal login returns the /me payload (truthy
      // but without mfaRequired).
      if (result && result.mfaRequired === true) {
        setMfaChallenge(result);
        setMfaCode('');
        return;
      }
      navigate('/subscription', { replace: true });
    } catch (err) {
      // The CODE_MAP / MESSAGE_MAP in notify.js translates
      // AUTH_INVALID_CREDENTIALS, INVALID_CREDENTIALS, "invalid
      // credentials", 401 status, and INVALID_EMAIL_DOMAIN into
      // friendly copy. The fallback here only kicks in if backend
      // returns something the mappings don't recognise.
      notifyError(err, { title: 'Login failed' });
    } finally {
      setSubmitting(false);
    }
  }

  async function onMfaVerify() {
    if (!mfaChallenge || !mfaChallenge.challengeId) return;
    if (!/^\d{6}$/.test(mfaCode)) {
      notifyError(
        { code: 'MFA_INVALID_CODE' },
        { title: 'Enter the 6-digit code' }
      );
      return;
    }
    setSubmitting(true);
    try {
      await verifyLoginMfa({
        challengeId: mfaChallenge.challengeId,
        code: mfaCode
      });
      navigate('/subscription', { replace: true });
    } catch (err) {
      // notify.js maps MFA_INVALID_CODE / MFA_TOO_MANY_ATTEMPTS /
      // MFA_CHALLENGE_EXPIRED to the right copy. If the challenge has
      // expired we kick the user back to the password step so they
      // start a fresh login.
      const code =
        err &&
        err.response &&
        err.response.data &&
        err.response.data.error &&
        err.response.data.error.code;
      notifyError(err, { title: 'MFA verification failed' });
      if (code === 'MFA_CHALLENGE_EXPIRED' || code === 'MFA_TOO_MANY_ATTEMPTS') {
        setMfaChallenge(null);
        setMfaCode('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function backToLogin() {
    setMfaChallenge(null);
    setMfaCode('');
  }

  // ------------------------ MFA code step ----------------------------
  if (mfaChallenge) {
    return (
      <div className="gi-onboarding-rise w-full bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
        {/* Key glyph signals "enter the one-time code" — distinct from
            the password step's shield-and-check. */}
        <LoginBadge className="h-11 w-11" icon={<KeyOutlined />} />

        <div className="mt-5 mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
            Enter authentication code
          </h2>
          <p className="text-sm text-ink-500 m-0 mt-1.5">
            Contact your administrator.
          </p>
        </div>

        <Form
          layout="vertical"
          disabled={submitting}
          size="large"
          className="onboarding-compact-form"
          onFinish={onMfaVerify}
        >
          <Form.Item
            label={
              <span className="text-sm font-semibold text-ink-800">
                6-digit code
              </span>
            }
            required
          >
            <Input
              ref={codeInputRef}
              prefix={<KeyOutlined className="text-ink-400" />}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => {
                // Strip everything that isn't a digit; AntD's Input
                // doesn't support pattern-only filtering natively.
                const next = String(e.target.value || '').replace(/\D/g, '').slice(0, 6);
                setMfaCode(next);
              }}
              onPressEnter={onMfaVerify}
            />
          </Form.Item>

          <Form.Item className="!mb-0 !mt-1">
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              disabled={mfaCode.length !== 6 || submitting}
              className="!h-12 !text-base !font-semibold !shadow-[0_8px_20px_-8px_rgba(24,119,242,0.55)] hover:!shadow-[0_10px_24px_-8px_rgba(24,119,242,0.65)] active:!shadow-[0_4px_12px_-4px_rgba(24,119,242,0.55)] transition-shadow"
            >
              Verify and continue
            </Button>
          </Form.Item>
        </Form>

        <div className="mt-5 pt-4 border-t border-surface-200 flex justify-center">
          <button
            type="button"
            onClick={backToLogin}
            disabled={submitting}
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-surface-200 bg-white text-sm font-semibold text-ink-700 hover:text-brand-600 hover:border-brand-200 hover:bg-brand-50/40 active:bg-brand-50 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm hover:shadow"
          >
            <ArrowLeftOutlined className="text-ink-400 group-hover:text-brand-500 transition-colors group-hover:-translate-x-0.5 transform duration-150" />
            Back to login
          </button>
        </div>
      </div>
    );
  }

  // ------------------------ password step ----------------------------
  return (
    <div className="w-full bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
      {/* Login badge — shield-and-check glyph signals "secure sign-in"
          without the generic page-y feel of the global brand mark. */}
      <LoginBadge className="h-11 w-11" icon={<SafetyCertificateOutlined />} />

      <div className="mt-5 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
          Welcome back
        </h2>
        <p className="text-sm text-ink-500 m-0 mt-1.5">
          Log in with your{' '}
          <code className="text-xs bg-surface-100 px-1.5 py-0.5 rounded">
            @master.glass-i
          </code>{' '}
          or{' '}
          <code className="text-xs bg-surface-100 px-1.5 py-0.5 rounded">
            @user.glass-i
          </code>{' '}
          email.
        </p>
      </div>

      <Form
        layout="vertical"
        onFinish={onFinish}
        disabled={submitting}
        size="large"
        className="onboarding-compact-form"
      >
        <Form.Item
          label={
            <span className="text-sm font-semibold text-ink-800">Email</span>
          }
          name="email"
          rules={[
            { required: true, message: 'email is required' },
            {
              pattern: /^.+@(master|user)\.glass-i$/,
              message: 'must end with @master.glass-i or @user.glass-i'
            }
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-ink-400" />}
            placeholder="ravi@master.glass-i"
            autoComplete="username"
          />
        </Form.Item>

        <Form.Item
          label={
            <div className="flex items-center justify-between w-full gap-3">
              <span className="text-sm font-semibold text-ink-800">
                Password
              </span>
              {/* Stage 5N-6: tenant Forgot Password flow. The reset
                  backend is now live (POST /api/auth/forgot-password
                  + /reset-password) so this link goes to the public
                  page that drives the request stage. */}
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-brand-500 hover:text-brand-600 transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          }
          name="password"
          rules={[{ required: true, message: 'password is required' }]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-ink-400" />}
            autoComplete="current-password"
            placeholder="Your password"
          />
        </Form.Item>

        <Form.Item className="!mb-0 !mt-1">
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            block
            className="!h-12 !text-base !font-semibold !shadow-[0_8px_20px_-8px_rgba(24,119,242,0.55)] hover:!shadow-[0_10px_24px_-8px_rgba(24,119,242,0.65)] active:!shadow-[0_4px_12px_-4px_rgba(24,119,242,0.55)] transition-shadow"
          >
            Log in
          </Button>
        </Form.Item>
      </Form>

      {/* Trust strip — emerald-tinted, soft icon background, reads
          as reassuring rather than decorative. */}
      <div className="mt-5 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3.5 py-2.5 flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
          <SafetyOutlined />
        </span>
        <p className="text-sm text-ink-700 m-0 leading-snug">
          Protected with enterprise-grade security
        </p>
      </div>

      {/* Secondary CTA — clearly distinct from the primary Log in
          action: muted line, borderless, action word as a link. */}
      <div className="mt-5 pt-4 border-t border-surface-200 text-center">
        <p className="text-sm text-ink-500 m-0">
          New to Glass-i?{' '}
          <Link
            to="/signup"
            className="font-semibold text-brand-500 hover:text-brand-600 transition-colors"
          >
            Start signup →
          </Link>
        </p>
      </div>

      {/* Fine print — links to the new public legal pages. Kept
          small and neutral so it doesn't compete with the primary
          Log in action above. */}
      <p className="text-xs text-ink-400 m-0 mt-4 text-center">
        By logging in you agree to our{' '}
        <Link
          to="/terms"
          className="hover:text-ink-700 underline-offset-2 hover:underline"
        >
          Terms
        </Link>{' '}
        and{' '}
        <Link
          to="/privacy"
          className="hover:text-ink-700 underline-offset-2 hover:underline"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
